import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const WEEKDAYS = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];

const WEATHER_CODE_MAP = {
  0: '晴天',
  1: '大部晴朗',
  2: '部分多云',
  3: '阴天',
  45: '雾',
  48: '雾',
  51: '小雨',
  53: '中雨',
  55: '大雨',
  56: '冻雨',
  57: '强冻雨',
  61: '小雨',
  63: '中雨',
  65: '大雨',
  66: '冻雨',
  67: '强冻雨',
  71: '小雪',
  73: '中雪',
  75: '大雪',
  77: '雪粒',
  80: '小雨',
  81: '中雨',
  82: '大雨',
  85: '小雪',
  86: '大雪',
  95: '雷暴',
  96: '雷暴冰雹',
  99: '强雷暴冰雹'
};

const CITY_COORDS = {
  4401: { name: '广州', lat: 23.1291, lon: 113.2644 },
  4403: { name: '深圳', lat: 22.5431, lon: 114.0579 },
  4404: { name: '珠海', lat: 22.2707, lon: 113.5767 },
  4406: { name: '佛山', lat: 23.0215, lon: 113.1219 },
  4413: { name: '惠州', lat: 23.1115, lon: 114.4152 },
  4419: { name: '东莞', lat: 23.043, lon: 113.7633 },
  4420: { name: '中山', lat: 22.517, lon: 113.3927 }
};

const DEFAULT_CITY = CITY_COORDS[4401];

const emptyWeather = {
  temperature: null,
  tempMin: null,
  tempMax: null,
  weatherCode: null,
  humidity: null,
  windKmh: null,
  visibilityM: null,
  uvIndex: null,
  usAqi: null
};

function toFinite(raw) {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function windLevelText(kmh) {
  const n = toFinite(kmh);
  if (n == null) return '--';
  const ms = n / 3.6;
  const table = [0.3, 1.6, 3.4, 5.5, 8.0, 10.8, 13.9, 17.2, 20.8, 24.5, 28.5, 32.7];
  let level = 12;
  for (let i = 0; i < table.length; i += 1) {
    if (ms < table[i]) {
      level = i;
      break;
    }
  }
  return `${level}级`;
}

function uvText(uv) {
  const n = toFinite(uv);
  if (n == null) return '--';
  if (n < 3) return '弱';
  if (n < 6) return '中';
  if (n < 8) return '强';
  if (n < 11) return '很强';
  return '极强';
}

function aqiDisplay(aqi) {
  const n = toFinite(aqi);
  if (n == null) return { text: '--', cls: '' };
  if (n <= 50) return { text: '优', cls: 'good' };
  if (n <= 100) return { text: '良', cls: 'good' };
  if (n <= 150) return { text: '轻度', cls: 'warn' };
  if (n <= 200) return { text: '中度', cls: 'warn' };
  if (n <= 300) return { text: '重度', cls: 'bad' };
  return { text: '严重', cls: 'bad' };
}

function visibilityText(meters) {
  const n = toFinite(meters);
  if (n == null) return '--';
  const km = n / 1000;
  if (km >= 1) return `${Math.round(km)}km`;
  return `${Math.round(n)}m`;
}

function weatherIcon(code) {
  const n = toFinite(code);
  if (n == null) return { emoji: '☀', sunny: true };
  if (n === 0 || n === 1) return { emoji: '☀', sunny: true };
  if (n === 2) return { emoji: '⛅', sunny: false };
  if (n === 3) return { emoji: '☁', sunny: false };
  if (n === 45 || n === 48) return { emoji: '🌫', sunny: false };
  if (n === 71 || n === 73 || n === 75 || n === 77 || n === 85 || n === 86) {
    return { emoji: '❄', sunny: false };
  }
  if (n === 95 || n === 96 || n === 99) return { emoji: '⛈', sunny: false };
  return { emoji: '🌧', sunny: false };
}

function toDisplayTemp(celsius, isCelsius) {
  const n = toFinite(celsius);
  if (n == null) return '--';
  return isCelsius ? Math.round(n) : Math.round((n * 9) / 5 + 32);
}

async function fetchForecast(lat, lon) {
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    current: 'temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m,visibility,uv_index',
    daily: 'temperature_2m_max,temperature_2m_min',
    timezone: 'Asia/Shanghai',
    forecast_days: '1'
  });
  const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`);
  if (!res.ok) throw new Error('forecast');
  return res.json();
}

async function fetchAir(lat, lon) {
  const params = new URLSearchParams({
    latitude: String(lat),
    longitude: String(lon),
    current: 'us_aqi',
    timezone: 'Asia/Shanghai'
  });
  const res = await fetch(`https://air-quality-api.open-meteo.com/v1/air-quality?${params}`);
  if (!res.ok) throw new Error('air');
  return res.json();
}

function WeatherWidget({ storeId }) {
  const storeList = useSelector((state) => state.dashboard?.storeList);
  const [isCelsius, setIsCelsius] = useState(true);
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [weather, setWeather] = useState(emptyWeather);

  const selectedStoreId = storeId != null && String(storeId).trim() !== '' ? String(storeId).trim() : '';
  const stores = Array.isArray(storeList?.data) ? storeList.data : [];
  const store = stores.find((item) => String(item.store_id) === selectedStoreId);
  const city = CITY_COORDS[store?.city_id] || DEFAULT_CITY;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!selectedStoreId) return undefined;
    let cancelled = false;
    const pull = async () => {
      try {
        const [forecastRes, airRes] = await Promise.allSettled([
          fetchForecast(city.lat, city.lon),
          fetchAir(city.lat, city.lon)
        ]);
        if (cancelled) return;
        const next = { ...emptyWeather };
        if (forecastRes.status === 'fulfilled') {
          const current = forecastRes.value?.current || {};
          const daily = forecastRes.value?.daily || {};
          next.temperature = toFinite(current.temperature_2m);
          next.humidity = toFinite(current.relative_humidity_2m);
          next.weatherCode = toFinite(current.weather_code);
          next.windKmh = toFinite(current.wind_speed_10m);
          next.visibilityM = toFinite(current.visibility);
          next.uvIndex = toFinite(current.uv_index);
          next.tempMax = toFinite(Array.isArray(daily.temperature_2m_max) ? daily.temperature_2m_max[0] : null);
          next.tempMin = toFinite(Array.isArray(daily.temperature_2m_min) ? daily.temperature_2m_min[0] : null);
        }
        if (airRes.status === 'fulfilled') {
          next.usAqi = toFinite(airRes.value?.current?.us_aqi);
        }
        setWeather(next);
      } catch {
        if (!cancelled) setWeather(emptyWeather);
      }
    };
    pull();
    const timer = setInterval(pull, 60 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [selectedStoreId, city.lat, city.lon]);

  const dayName = WEEKDAYS[currentTime.getDay()];
  const timeStr = currentTime.toLocaleTimeString('zh-CN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  const displayTemp = toDisplayTemp(weather.temperature, isCelsius);
  const minText = toDisplayTemp(weather.tempMin, isCelsius);
  const maxText = toDisplayTemp(weather.tempMax, isCelsius);
  const desc = WEATHER_CODE_MAP[weather.weatherCode] || '--';
  const rangeText = (minText === '--' || maxText === '--') ? '--' : `${minText}°~${maxText}°`;
  const icon = weatherIcon(weather.weatherCode);
  const humidityText = weather.humidity == null ? '--' : `${Math.round(weather.humidity)}%`;
  const air = aqiDisplay(weather.usAqi);

  return (
    <div className="weather-widget">
      <div className="card-shine"></div>
      <div className="w-top">
        <div className="w-top-left">
          <div className="w-location">{city.name}</div>
          <div className="w-day">{dayName} {timeStr}</div>
        </div>
        <div className="w-top-right">
          <div className={`w-icon${icon.sunny ? ' sun' : ''}`}>{icon.emoji}</div>
          <div className="w-temp-col">
            <div className="w-temp-row">
              <span className="w-temp">{displayTemp === '--' ? '--' : `${displayTemp}°`}</span>
              <div className="temp-toggle">
                <button type="button" className={isCelsius ? 'active' : ''} onClick={() => setIsCelsius(true)}>°C</button>
                <button type="button" className={!isCelsius ? 'active' : ''} onClick={() => setIsCelsius(false)}>°F</button>
              </div>
            </div>
            <div className="w-condition">{`${desc} · ${rangeText}`}</div>
          </div>
        </div>
      </div>
      <div className="w-details">
        <div className="w-detail-item">
          <span className="w-detail-label">湿度</span>
          <span className="w-detail-value">{humidityText}</span>
        </div>
        <div className="w-detail-divider"></div>
        <div className="w-detail-item">
          <span className="w-detail-label">风速</span>
          <span className="w-detail-value">{windLevelText(weather.windKmh)}</span>
        </div>
        <div className="w-detail-divider"></div>
        <div className="w-detail-item">
          <span className="w-detail-label">空气</span>
          <span className={`w-detail-value${air.cls ? ` ${air.cls}` : ''}`}>{air.text}</span>
        </div>
        <div className="w-detail-divider"></div>
        <div className="w-detail-item">
          <span className="w-detail-label">紫外线</span>
          <span className="w-detail-value">{uvText(weather.uvIndex)}</span>
        </div>
        <div className="w-detail-divider"></div>
        <div className="w-detail-item">
          <span className="w-detail-label">能见度</span>
          <span className="w-detail-value">{visibilityText(weather.visibilityM)}</span>
        </div>
      </div>
    </div>
  );
}

export default WeatherWidget;
